import * as React from "react";
import { IProduct } from "~/types/product";
import * as searchActions from "~/store/reducers/search/search-actions";
import store from "~/store";
import { IRootState } from "~/store/reducers";
import { ISearchState, ISearchParams } from "~/store/reducers/search/search-reducer";
import { connect } from "react-redux";
import Button from "../button/button";
import ProductGallery, { IProductGalleryProps } from "../product-gallery/product-gallery";
import { ISearchRequest } from "~/store/reducers/search/search-actions";
import { updateUrlParameters, getUrlParameters } from "~/utils/url-utils";
import { useEffect } from "react";
import Icon from "../icons/icon";
import FacetForm, { IFacetFormProps } from "./facet-form/facet-form";
import classNames from "classnames";
import { toggleBodyClass } from "~/utils/body-class-util";
import SortingForm, { ISortingFormProps } from "./sorting-form/sorting-form";
import Anchor from "../anchor/anchor";

interface ISearchResultsPropsFromStore {
    productsFromStore?: IProduct[];
    sortingFromStore?: string;
    facetsFromStore?: string[];
    pageFromStore?: number;
    productsTotalCountFromStore?: number;
    productsTotalCountLabelFromStore?: string;
    displayedProductsOutOfTotalLabelFromStore?: string;
    anchorId?: string;
}

export interface ISearchResultsProps
    extends Omit<ISearchState, "searchParams" | "products">,
        ISortingFormProps,
        IFacetFormProps,
        IProductGalleryProps,
        ISearchResultsPropsFromStore {
    loadMoreBtnLabel: string;
    loadMoreWebApiUrl: string;
    searchQueryRichText: string;
    facetModalButtonLabel?: string;
    facetsNoResultsRichText?: string;
}

export type SearchResultsProps = ISearchResultsProps & ISearchParams;

const performSearch = (params: ISearchRequest) => {
    store.dispatch(searchActions.searchRequest(params));
};

const SearchResults: React.FC<SearchResultsProps> = ({
    isSearchPending,
    productsFromStore,
    loadMoreWebApiUrl,
    loadMoreBtnLabel,
    loadMorePageSize,
    secondaryPriceLabel,
    // addToBasketWebApiUrl,
    productsTotalCountFromStore,
    displayedProductsOutOfTotalLabelFromStore,
    productsTotalCountLabelFromStore,
    searchQueryRichText,
    themeCodes,
    searchString,
    complexQuery,
    pageFromStore,
    facetsNoResultsRichText,
    facetsSeeMoreBtnLabel,
    facetsSeeLessBtnLabel,
    facetsFromStore,
    facetGroups,
    facetModalButtonLabel,
    facetGroupsTitle,
    facetGroupsConfirmButtonLabel,
    sortingOptions,
    sortingFromStore,
    anchorId,
    ...props
}) => {
    const [initialLoad, setInitialLoad] = React.useState(true);
    const [isFacetModalOpen, setIsFacetModalOpen] = React.useState(false);

    const toggleFacetModal = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>, toggleOn: boolean) => {
        event?.preventDefault();
        setIsFacetModalOpen(toggleOn);
        toggleBodyClass("body--facetOverlayOpen", toggleOn);
    };

    const products = productsFromStore || props.products;
    const page = pageFromStore != undefined ? pageFromStore : props.page;
    const sorting = sortingFromStore || props.sorting;
    const facets = facetsFromStore || props.facets;
    const displayedProductsOutOfTotalLabel =
        displayedProductsOutOfTotalLabelFromStore || props.displayedProductsOutOfTotalLabel;
    const productsTotalCountLabel = productsTotalCountLabelFromStore || props.productsTotalCountLabel;
    const productsTotalCount = productsTotalCountFromStore || props.productsTotalCount;
    // TODO: Get rid of non-null assertion
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const showLoadMoreBtn = productsTotalCount! > products.length;

    // Populate store with existing results and set search parameters
    React.useEffect(() => {
        const searchParams: ISearchParams = {
            page,
            loadMorePageSize,
            themeCodes,
            searchString,
            complexQuery,
            facets,
            sorting
        };

        store.dispatch(searchActions.updateSearchParams(searchParams));
        store.dispatch(searchActions.populateSearch({ products: props.products }));
    }, []);

    // Update page URL parameter
    useEffect(() => {
        const searchUrlParams: { [key: string]: string } = {};
        searchUrlParams.page = String(page);

        updateUrlParametersOnEffect(searchUrlParams);
    }, [page]);

    // Update sorting URL parameter
    useEffect(() => {
        const searchUrlParams: { [key: string]: string } = {};
        searchUrlParams.page = String(0);

        if (sorting) {
            searchUrlParams.sorting = sorting;
        }

        updateUrlParametersOnEffect(searchUrlParams);
    }, [sorting]);

    // Update facets URL parameter
    useEffect(() => {
        const searchUrlParams: { [key: string]: string } = {};
        searchUrlParams.page = String(0);

        if (facets) {
            let facetUrlString = "";
            if (facets.length > 0) {
                facetUrlString = facets.reduce((accumulator, currentValue) => {
                    return accumulator + `_${currentValue}`;
                });
            }
            searchUrlParams.facets = facetUrlString;
        }

        updateUrlParametersOnEffect(searchUrlParams);
    }, [facets]);

    const updateUrlParametersOnEffect = (searchUrlParams: { [key: string]: string }) => {
        // Bail out on initial load to keep url clean until parameters change
        if (initialLoad) {
            setInitialLoad(false);
            return;
        }

        const existingUrlParams = getUrlParameters();

        // Combine search params with existing url params and update url
        updateUrlParameters(
            {
                ...existingUrlParams,
                ...searchUrlParams
            },
            true
        );
    };

    const onLoadMore = () =>
        performSearch({
            apiUrl: loadMoreWebApiUrl,
            // TODO: Get rid of non-null assertion
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            page: page! + 1
        });

    const facetsInitialValues: { [key: string]: any } = {};
    if (props.facets && props.facets.length > 0) {
        props.facets.forEach(currentValue => (facetsInitialValues[currentValue] = true));
    }

    const sortingInitialValues = { sorting: props.sorting };

    const baseClass = "search-results";
    const componentClasses = classNames(baseClass, {
        "anchor-wrapper": !!anchorId,
        [`${baseClass}--hasFacets`]: facetGroups
    });

    return (
        <section className={componentClasses}>
            {anchorId && <Anchor id={anchorId} />}
            {searchQueryRichText && (
                <div className={`${baseClass}__inner`}>
                    <div
                        className={`${baseClass}__search-rich-text content-rich-text`}
                        dangerouslySetInnerHTML={{ __html: searchQueryRichText }}
                    />
                </div>
            )}
            <div className={`${baseClass}__inner`}>
                {facetGroups && (
                    <FacetForm
                        initialValues={facetsInitialValues}
                        closeFacetModal={e => toggleFacetModal(e, false)}
                        loadMoreWebApiUrl={loadMoreWebApiUrl}
                        facets={facets}
                        facetGroups={facetGroups}
                        facetsSeeMoreBtnLabel={facetsSeeMoreBtnLabel}
                        facetsSeeLessBtnLabel={facetsSeeLessBtnLabel}
                        isFacetModalOpen={isFacetModalOpen}
                        facetGroupsTitle={facetGroupsTitle}
                        facetGroupsConfirmButtonLabel={facetGroupsConfirmButtonLabel}
                    />
                )}
                <div className={`${baseClass}__products`}>
                    <div className={`${baseClass}__grid-align`}>
                        <div className={`${baseClass}__top`}>
                            {productsTotalCountLabel && (
                                <div className={`${baseClass}__total-results`}>{productsTotalCountLabel}</div>
                            )}
                            <button
                                className={`${baseClass}__open-facet-modal-btn reset-button`}
                                onClick={e => toggleFacetModal(e, true)}
                            >
                                <Icon iconId="icon_placeholder_filtering" />
                                <span>{facetModalButtonLabel}</span>
                            </button>
                            {sortingOptions && (
                                <div className={`${baseClass}__sorting-wrapper`}>
                                    <SortingForm
                                        initialValues={sortingInitialValues}
                                        loadMoreWebApiUrl={loadMoreWebApiUrl}
                                        sortingOptions={sortingOptions}
                                        sorting={sorting}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                    {products.length > 0 ? (
                        <>
                            <ProductGallery
                                products={products}
                                secondaryPriceLabel={secondaryPriceLabel}
                                // addToBasketWebApiUrl={addToBasketWebApiUrl}
                                trackingList="Search Results"
                                disableCentering
                            />
                            {displayedProductsOutOfTotalLabel && (
                                <div className={`${baseClass}__x-of-y`}>
                                    {displayedProductsOutOfTotalLabel}
                                </div>
                            )}
                            {showLoadMoreBtn && (
                                <div className={`${baseClass}__button-wrapper`}>
                                    <Button
                                        className={`${baseClass}__button button--inverted`}
                                        isLarge
                                        onClick={onLoadMore}
                                        disabled={isSearchPending}
                                    >
                                        {loadMoreBtnLabel}
                                    </Button>
                                </div>
                            )}
                        </>
                    ) : (
                        <div className={`${baseClass}__facets-no-results`}>
                            {facetsNoResultsRichText && (
                                <div dangerouslySetInnerHTML={{ __html: facetsNoResultsRichText }} />
                            )}
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
};

const mapStateToProps = ({ search }: IRootState) => {
    const {
        searchParams: { page, sorting, facets },
        isSearchPending,
        products,
        displayedProductsOutOfTotalLabel,
        productsTotalCount,
        productsTotalCountLabel
    } = search;
    return {
        isSearchPending,
        productsFromStore: products,
        displayedProductsOutOfTotalLabelFromStore: displayedProductsOutOfTotalLabel,
        pageFromStore: page,
        sortingFromStore: sorting,
        facetsFromStore: facets,
        productsTotalCountFromStore: productsTotalCount,
        productsTotalCountLabelFromStore: productsTotalCountLabel
    };
};

export default connect(mapStateToProps)(SearchResults);
